import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/axios';
import { useAuth } from '@/context/AuthContext';

const TenantContext = createContext(null);

/**
 * Provides the list of tenants the current user can access + the
 * currently active tenant id. Components can read + switch via useTenant().
 *
 * Active tenant id is persisted to localStorage so the axios interceptor
 * can attach it on every request.
 */
export function TenantProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantIdState] = useState(
    localStorage.getItem('active_tenant_id') || null,
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadTenants = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user) {
      setTenants([]);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/tenants/me');
      setTenants(res.data.available_tenants || []);
      setIsSuperAdmin(!!res.data.is_super_admin);
      const serverActive = res.data.active_tenant_id;
      const stored = localStorage.getItem('active_tenant_id');
      const valid = (res.data.available_tenants || []).map((t) => t.id);
      // Prefer stored value if still accessible, otherwise server suggestion
      if (stored && valid.includes(stored)) {
        setActiveTenantIdState(stored);
      } else if (serverActive && valid.includes(serverActive)) {
        localStorage.setItem('active_tenant_id', serverActive);
        setActiveTenantIdState(serverActive);
      } else if (valid.length > 0) {
        localStorage.setItem('active_tenant_id', valid[0]);
        setActiveTenantIdState(valid[0]);
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
      setTenants([]);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const switchTenant = useCallback((tenantId) => {
    localStorage.setItem('active_tenant_id', tenantId);
    setActiveTenantIdState(tenantId);
    // Hard reload to reset all in-memory state / caches
    window.location.reload();
  }, []);

  const activeTenant = tenants.find((t) => t.id === activeTenantId) || null;

  return (
    <TenantContext.Provider
      value={{
        tenants,
        activeTenantId,
        activeTenant,
        isSuperAdmin,
        loading,
        switchTenant,
        refresh: loadTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within <TenantProvider>');
  }
  return ctx;
}
